/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @angular-eslint/prefer-on-push-component-change-detection */
/* eslint-disable @angular-eslint/prefer-signals */

import { Component, inject, Input, OnInit, Injector, runInInjectionContext } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Timestamp } from '@angular/fire/firestore';
import { AnimationOptions } from 'ngx-lottie';
import { finalize, first, from, map, Observable, of, switchMap, tap } from 'rxjs';

import type {
  PlatformMediaFile,
  PlatformMediaCustomableName,
} from '../../../../../../shared/db-models/media';

@Component({
  selector: 'app-media-file-manager',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
  styleUrls: [],
  templateUrl: './template.html',
})
export class MediaFileManagerComponent implements OnInit {
  private _fs: AngularFirestore;
  private injector: Injector;

  @Input() platformMediaCustomableName: PlatformMediaCustomableName;

  selectedFile: File | null = null;
  uploadProgress$: Observable<number | undefined> | undefined;

  private _collection: ReturnType<AngularFirestore['collection']>;
  private _doc: ReturnType<MediaFileManagerComponent['_collection']['doc']>;
  public docData$: Observable<PlatformMediaFile>;
  public fetchFile$: Observable<string>;

  get fileName() {
    return this.selectedFile?.name;
  }
  startWith(match: string, str: string) {
    return str.startsWith(match);
  }

  public lottieOptions: AnimationOptions;

  private storage: AngularFireStorage;

  constructor() {
    this.storage = inject(AngularFireStorage);
    this._fs = inject(AngularFirestore);
    this.injector = inject(Injector);
    // Use compat API within injection context to avoid injection errors
    runInInjectionContext(this.injector, () => {
      this._collection = this._fs.collection<PlatformMediaFile>('media');
    });
  }

  ngOnInit() {
    if (!this.platformMediaCustomableName) return;
    // Use compat API within injection context to avoid injection errors
    runInInjectionContext(this.injector, () => {
      this._doc = this._collection.doc<PlatformMediaFile>(this.platformMediaCustomableName);
      this.docData$ = this._doc.valueChanges() as Observable<PlatformMediaFile>;
    });
    this.fetchFile$ = this.docData$.pipe(
      switchMap((data) => {
        if (data.mediaKind.includes('json')) return from(fetch(data.url).then((res) => res.json()));
        return of(null);
      }),
      tap(async (data) => {
        this.lottieOptions = { animationData: data };
      }),
      map((data) => JSON.stringify(data))
    );
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;
    const file = files[0] ?? null;
    const fileEnding = file.name.split('.').pop();
    const renamedFile = new File([file], `${this.platformMediaCustomableName}.${fileEnding}`, {
      type: file.type,
    });
    this.selectedFile = renamedFile;
  }

  onUpload() {
    if (this.selectedFile) {
      const filePath = `media/${this.platformMediaCustomableName}`;
      const fileRef = this.storage.ref(filePath);
      const task = this.storage.upload(filePath, this.selectedFile);
      this.uploadProgress$ = task.percentageChanges();
      task
        .snapshotChanges()
        .pipe(
          finalize(() => {
            fileRef
              .getDownloadURL()
              .pipe(first())
              .subscribe((url) => {
                // Use compat API within injection context to avoid injection errors
                runInInjectionContext(this.injector, () => {
                  this._doc.set({
                    key: this.platformMediaCustomableName,
                    mediaKind: this.selectedFile?.type ?? 'application/json',
                    uploadedAt: Timestamp.now(),
                    url,
                  } as PlatformMediaFile);
                });
              });
          })
        )
        .subscribe();
    }
  }

  onClear() {
    const filePath = `media/${this.platformMediaCustomableName}`;
    this.storage
      .ref(filePath)
      .delete()
      .subscribe(() => {
        // Use compat API within injection context to avoid injection errors
        runInInjectionContext(this.injector, () => {
          this._doc.delete();
        });
      });
    this.uploadProgress$ = undefined;
  }
}
